import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Trash2, Loader2, ChevronLeft, ChevronRight, ArrowUpRight, Check, ImageIcon } from 'lucide-react';
import { PCCase } from '../types';
import { fetchCases, deleteCase, PCCaseQueryResult } from '../services/caseService';
import { CaseEditModal } from '../components/admin/CaseEditModal';
import { Badge, Button, SearchInput, ToggleGroup } from '../components/ui';
import { useSEO } from '../hooks/useSEO';

const pageSize = 50;

export const Admin: React.FC = () => {
  useSEO({ title: 'Admin', noindex: true });
  const [cases, setCases] = useState<PCCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [editingCase, setEditingCase] = useState<PCCase | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [counts, setCounts] = useState<{ total: number; pending: number } | null>(null);

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const result: PCCaseQueryResult = await fetchCases({
        page,
        pageSize,
        search: search || undefined,
        sortBy: 'name',
        sortOrder: 'asc',
        ...(!showAll ? { allSet: false } : {}),
      });
      setCases(result.data);
      setTotalPages(result.totalPages);
      setTotalCount(result.count);
    } catch (error) {
      console.error('Failed to load cases:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search, showAll]);

  // Review progress across the whole database (ignores search)
  const loadCounts = useCallback(async () => {
    try {
      const [all, pending] = await Promise.all([
        fetchCases({ page: 1, pageSize: 1 }),
        fetchCases({ page: 1, pageSize: 1, allSet: false }),
      ]);
      setCounts({ total: all.count, pending: pending.count });
    } catch (error) {
      console.error('Failed to load review counts:', error);
    }
  }, []);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleDelete = async (pcCase: PCCase) => {
    if (!confirm(`Delete ${pcCase.manufacturer} ${pcCase.name}? This cannot be undone.`)) return;
    setDeletingId(pcCase.id);
    try {
      await deleteCase(pcCase.id);
      await Promise.all([loadCases(), loadCounts()]);
    } catch (error) {
      console.error('Failed to delete case:', error);
      alert('Failed to delete case');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditSave = () => {
    setEditingCase(null);
    loadCases();
    loadCounts();
  };

  const reviewed = counts ? counts.total - counts.pending : 0;
  const progress = counts && counts.total > 0 ? Math.round((reviewed / counts.total) * 100) : 0;

  return (
    <div className="theme-light min-h-screen bg-canvas text-fg">
      {/* Header */}
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5" aria-label="ComponentScale home">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" aria-hidden="true">
              <rect x="4" y="3" width="10" height="18" rx="1.5" stroke="currentColor" />
              <path d="M7 7h4" stroke="currentColor" />
              <path d="M18.5 3v18M17 3h3M17 21h3" className="stroke-accent" />
            </svg>
            <span className="font-semibold tracking-tight">ComponentScale</span>
          </Link>
          <Badge>Admin</Badge>
          <Link to="/" className="ml-auto inline-flex items-center gap-1 text-sm text-muted hover:text-fg transition-colors">
            View site
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6">
        {/* Title + review progress */}
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <h1 className="text-3xl font-semibold tracking-tightest">Cases</h1>
            <p className="mt-1 text-sm text-muted">Review extracted specs and mark each case as all set.</p>
          </div>
          <dl className="flex gap-8">
            <Stat label="Total" value={counts?.total} />
            <Stat label="Reviewed" value={counts ? reviewed : undefined} />
            <Stat label="Pending" value={counts?.pending} accent />
          </dl>
        </div>

        {counts && (
          <div className="flex items-center gap-3">
            <div
              className="h-1.5 flex-1 overflow-hidden rounded-full bg-line"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Review progress"
            >
              <div className="h-full rounded-full bg-fg transition-[width] duration-300" style={{ width: `${progress}%` }} />
            </div>
            <span className="font-mono text-xs text-muted">{progress}% reviewed</span>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full sm:w-80">
            <SearchInput
              aria-label="Search cases"
              placeholder="Search cases…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ToggleGroup
            options={[
              { value: 'pending', label: 'Pending' },
              { value: 'all', label: 'All cases' },
            ]}
            value={showAll ? 'all' : 'pending'}
            onChange={(v) => { setShowAll(v === 'all'); setPage(1); }}
          />
          <span className="text-sm text-muted sm:ml-auto">
            <span className="font-mono text-fg">{totalCount}</span> {showAll ? 'cases' : 'pending'}
          </span>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-line bg-surface">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2 text-left">
                  <Th>Case</Th>
                  <Th>Size</Th>
                  <Th>Dimensions</Th>
                  <Th>Side panel</Th>
                  <Th>Year</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-14 text-center text-muted">
                      <Loader2 className="mr-2 inline-block animate-spin" size={18} />
                      Loading…
                    </td>
                  </tr>
                ) : cases.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-14 text-center text-muted">
                      {showAll || search ? 'No cases found.' : 'Nothing pending — every case is reviewed.'}
                    </td>
                  </tr>
                ) : (
                  cases.map((c) => {
                    const image = c.thumbnailUrl || c.imageUrl;
                    return (
                      <tr key={c.id} className="border-b border-line last:border-0 hover:bg-surface-2/60">
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-3">
                            <Thumbnail src={image} />
                            <div className="min-w-0">
                              <button
                                type="button"
                                onClick={() => setEditingCase(c)}
                                className="block max-w-[240px] truncate text-left font-medium hover:text-accent-ink"
                              >
                                {c.name}
                              </button>
                              <span className="font-mono text-2xs uppercase tracking-label text-muted">{c.manufacturer}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5"><Badge size="sm">{c.sizeCategory}</Badge></td>
                        <td className="px-4 py-2.5 font-mono text-xs">
                          {c.dimensions?.height && c.dimensions.width && c.dimensions.depth
                            ? `${c.dimensions.height} × ${c.dimensions.width} × ${c.dimensions.depth}`
                            : <span className="text-danger">missing</span>}
                        </td>
                        <td className="px-4 py-2.5 text-muted">{c.sidePanel.join(', ') || '—'}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-muted">{c.releaseYear || '—'}</td>
                        <td className="px-4 py-2.5">
                          {c.allSet ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium">
                              <Check size={13} />
                              Reviewed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-ink">
                              <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            <IconLink to={`/case/${c.id}`} label={`View ${c.name} on site`}>
                              <ArrowUpRight size={15} />
                            </IconLink>
                            <Button variant="ghost" size="icon-sm" onClick={() => setEditingCase(c)} aria-label={`Edit ${c.name}`}>
                              <Pencil size={15} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => handleDelete(c)}
                              disabled={deletingId === c.id}
                              aria-label={`Delete ${c.name}`}
                              className="hover:bg-danger-soft hover:text-danger"
                            >
                              {deletingId === c.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-line px-4 py-3">
              <span className="text-sm text-muted">
                <span className="font-mono">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)}</span> of{' '}
                <span className="font-mono">{totalCount}</span>
              </span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} aria-label="Previous page">
                  <ChevronLeft size={18} />
                </Button>
                <span className="px-2 font-mono text-sm">{page} / {totalPages}</span>
                <Button variant="ghost" size="icon" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} aria-label="Next page">
                  <ChevronRight size={18} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Edit Modal */}
      {editingCase && (
        <CaseEditModal
          pcCase={editingCase}
          onClose={() => setEditingCase(null)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

const Thumbnail: React.FC<{ src?: string }> = ({ src }) => {
  const [failed, setFailed] = useState(false);

  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border border-line bg-surface-2">
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} className="h-full w-full object-contain p-1 mix-blend-multiply" />
      ) : (
        <ImageIcon size={16} className={failed ? 'text-danger' : 'text-muted'} aria-label={failed ? 'Image failed to load' : 'No image'} />
      )}
    </span>
  );
};

const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <th scope="col" className={`px-4 py-3 font-mono text-2xs font-normal uppercase tracking-label text-muted ${className}`}>
    {children}
  </th>
);

const Stat: React.FC<{ label: string; value?: number; accent?: boolean }> = ({ label, value, accent = false }) => (
  <div className="flex flex-col gap-0.5">
    <dt className="text-xs text-muted">{label}</dt>
    <dd className={`font-mono text-2xl ${accent ? 'text-accent-ink' : ''}`}>{value ?? '—'}</dd>
  </div>
);

const IconLink: React.FC<{ to: string; label: string; children: React.ReactNode }> = ({ to, label, children }) => (
  <Link
    to={to}
    aria-label={label}
    title={label}
    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted hover:bg-surface-2 hover:text-fg transition-colors"
  >
    {children}
  </Link>
);
